import React, { useRef, useState, useEffect, useMemo } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Modal, Space, Tag } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { fetchUserByKodeGrup1, getSalesMasterDataUtama } from '../utils/sales';
import { useNavigate } from 'react-router-dom';
import { AuthorityCheck } from '../components/AuthorityCheck';
import { Button as AntdButton } from 'antd';
import { useAuth } from '../context/AuthContext';
import type { SortOrder } from 'antd/es/table/interface';
import useSWR from 'swr';

const getCloseOrderReasonText = (value: string) => {
    switch (value) {
        case '01': return 'Less Stock';
        case '02': return 'Overpriced';
        case '03': return 'Change Document';
        default: return value;
    }
};

const getStatusText = (statusValue: string): string => {
    switch (statusValue) {
        case '01': return 'Draft';
        case '02': return 'Approved';
        case '03': return 'Waiting for Approved';
        case '04': return 'Close / Complete';
        case '05': return 'Canceled';
        case '06': return 'Closed';
        default: return 'Unknown Status';
    }
};

export interface DataType {
    key: string;
    close_order_reason: string;
    user_approved: string;
    sales_person: string;
    sales_person_code: string;
    status: string;
    se_no: string;
    sales_date: string;
    so_no: string;
    trans_type: string;
    order_type: string;
    cs_code: string;
    cs_name: string;
    purchase_order_no: string;
    top: string;
    agreement_no: string;
    credit_limit_approval_status: string;
    sub_total: string;
    item_disc: string;
    total_a: string;
    vat: string;
    grand_total: string;
    delivery_address1: string;
    delivery_address2: string;
    contact_person: string;
    contact_phone: string;
    deliver_via: string;
    warehouse_group: string;
    approval_remark: string;
    outstanding_remark: string;
    remark: string;
    credit_limit_remaining: string;
    invoice_return_no: string;
    [key: string]: any;
}

export type DataIndex = keyof DataType;
const SalesPersonNameCell: React.FC<{ code: string }> = ({ code }) => {
    const { data } = useSWR(code ? `user-resolve-${code}` : null, () => fetchUserByKodeGrup1(code));
    return <span>{data?.nama || code}</span>;
};
export const useSalesBrowser = () => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [showAllWarehouse, setShowAllWarehouse] = useState(false);
    const [salesPersonLabels, setSalesPersonLabels] = useState<Record<string, string>>({});

    const warehouseFetcher = async (url: string) => {
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        });
        if (!response.ok) {
            throw new Error('Failed to fetch warehouse data');
        }
        return response.json();
    };
    const { data: warehouseData } = useSWR<any[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/warehouse-data`,
        warehouseFetcher
    );

    const fetcher = async (url: string) => {
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
        });
        if (!response.ok) {
            throw new Error('Failed to fetch sales data');
        }
        const data = await response.json();
        const dataWithKeys = data.map((item: any) => ({
            ...item,
            key: item.se_no,
            sales_person_code: item.sales_person,
            sales_person: item.sales_person,
        }));
        dataWithKeys.sort((a: DataType, b: DataType) => (b.sales_date || '').localeCompare(a.sales_date || ''));
        return dataWithKeys;
    };

    const { data: rawTableData, error, isLoading, mutate } = useSWR<DataType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales-master-utama`,
        fetcher
    );

    const tableData = useMemo(() => {
        if (!rawTableData) return [];
        const hasOtoritas77 = userData?.otoritas ? String(userData.otoritas).split(';').includes('77') : false;
        if (showAllWarehouse && hasOtoritas77) {
            return rawTableData;
        }
        return rawTableData.filter(item => item.warehouse_group === userData?.warehouse_code);
    }, [rawTableData, userData?.warehouse_code, showAllWarehouse, userData?.otoritas]);

    useEffect(() => {
        const resolveNames = async () => {
            const uniqueCodes = Array.from(new Set(tableData.map(i => i.sales_person))).filter(Boolean);
            const newLabels = { ...salesPersonLabels };
            let isChanged = false;
            for (const code of uniqueCodes) {
                if (!newLabels[code]) {
                    const res = await fetchUserByKodeGrup1(code);
                    if (res?.nama) { newLabels[code] = res.nama; isChanged = true; }
                }
            }
            if (isChanged) setSalesPersonLabels(newLabels);
        };
        if (tableData.length > 0) resolveNames();
    }, [tableData]);

    const handleCancel = async (record: DataType) => {
        if (!record.close_order_reason) return;

        const confirm = await new Promise<boolean>((resolve) => {
            Modal.confirm({
                title: 'Konfirmasi Pembatalan',
                content: `Apakah Anda yakin ingin membatalkan SO ${record.so_no}?`,
                okText: 'Ya',
                cancelText: 'Tidak',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });

        if (!confirm) return;

        try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cancel-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    se_no: record.se_no,
                    status: '06'
                })
            });

            mutate();

        } catch (error) {
            console.error('Pembatalan gagal:', error);
            Modal.error({
                title: 'Gagal Membatalkan',
                content: 'Terjadi kesalahan saat memproses pembatalan'
            });
        }
    };


    const handleEdit = (record: DataType) => {
        navigate('/dashboard/sales_estimation', { state: { editData: record } });
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex as string);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex as string);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            if (dataIndex === 'status') {
                const statusText = getStatusText(record.status);
                return statusText.toLowerCase().includes((value as string).toLowerCase());
            }
            if (dataIndex === 'warehouse_group') {
                const wh = warehouseData?.find(w => w.warehouse_code === record.warehouse_group);
                const whName = wh ? wh.warehouse_name : record.warehouse_group;
                return (whName || '').toLowerCase().includes((value as string).toLowerCase());
            }
            if (record[dataIndex] === null || record[dataIndex] === undefined) {
                return false;
            }
            if (dataIndex === 'sales_person') {
                return record.sales_person.toLowerCase().includes((value as string).toLowerCase());
            }
            return record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase());
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text, record) => {
            if (dataIndex === 'status') {
                return getStatusText(record.status);
            }
            if (dataIndex === 'warehouse_group') {
                const wh = warehouseData?.find(w => w.warehouse_code === record.warehouse_group);
                const whName = wh ? wh.warehouse_name : record.warehouse_group;
                return searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={whName ? whName.toString() : ''}
                    />
                ) : (
                    whName
                );
            }
            return searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            );
        },
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'User Approved',
            dataIndex: 'user_approved',
            key: 'user_approved',
            hidden: true,
        },
        {
            title: 'Sales Person',
            dataIndex: 'sales_person',
            key: 'sales_person',
            filters: Array.from(new Set(tableData.map(item => item.sales_person))).filter(Boolean).map(code => ({ text: salesPersonLabels[code] || code, value: code })),
            onFilter: (value: any, record: DataType) => record.sales_person === value,
            sorter: (a: DataType, b: DataType) => (a.sales_person || '').localeCompare(b.sales_person || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
            render: (text: string) => <SalesPersonNameCell code={text} />,
        },
        {
            title: 'Purchase Order',
            dataIndex: 'purchase_order_no',
            key: 'purchase_order_no',
            ...getColumnSearchProps('purchase_order_no'),
            sorter: (a: DataType, b: DataType) => (a.purchase_order_no || '').localeCompare(b.purchase_order_no || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
        },
        {
            title: 'SE No',
            dataIndex: 'se_no',
            key: 'se_no',
            ...getColumnSearchProps('se_no'),
            sorter: (a: DataType, b: DataType) => (a.se_no || '').localeCompare(b.se_no || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
        },
        {
            title: 'SO Date',
            dataIndex: 'sales_date',
            key: 'sales_date',
            ...getColumnSearchProps('sales_date'),
            defaultSortOrder: 'descend' as SortOrder,
            sorter: (a: DataType, b: DataType) => (a.sales_date || '').localeCompare(b.sales_date || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
        },
        {
            title: 'SO No',
            dataIndex: 'so_no',
            key: 'so_no',
            ...getColumnSearchProps('so_no'),
            sorter: (a: DataType, b: DataType) => (a.so_no || '').localeCompare(b.so_no || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
        },
        {
            title: 'Transaksi Type',
            dataIndex: 'trans_type',
            key: 'trans_type',
            hidden: true,
        },
        {
            title: 'Order Type',
            dataIndex: 'order_type',
            key: 'order_type',
            hidden: true,
        },
        {
            title: 'Customer Code',
            dataIndex: 'cs_code',
            key: 'cs_code',
            hidden: true,
        },
        {
            title: 'Customer Name',
            dataIndex: 'cs_name',
            key: 'cs_name',
            ...getColumnSearchProps('cs_name'),
            sorter: (a: DataType, b: DataType) => (a.cs_name || '').localeCompare(b.cs_name || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
        },
        {
            title: 'Close Order Reason',
            dataIndex: 'close_order_reason',
            key: 'close_order_reason',
            hidden: false,
            sorter: (a: DataType, b: DataType) => (a.close_order_reason || '').localeCompare(b.close_order_reason || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
            render: (text: string) => getCloseOrderReasonText(text),
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse_group',
            key: 'warehouse_group',
            filters: Array.from(new Set(tableData.map(item => item.warehouse_group)))
                .filter(Boolean)
                .map(code => {
                    const wh = warehouseData?.find(w => w.warehouse_code === code);
                    return { text: wh ? wh.warehouse_name : code, value: code };
                }),
            onFilter: (value: any, record: DataType) => record.warehouse_group === value,
            render: (text: string) => {
                const wh = warehouseData?.find(w => w.warehouse_code === text);
                return wh ? wh.warehouse_name : text;
            },
            sorter: (a: DataType, b: DataType) => (a.warehouse_group || '').localeCompare(b.warehouse_group || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
        },
        {
            title: 'Approval Status',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'Draft', value: '01' },
                { text: 'Approved', value: '02' },
                { text: 'Waiting for Approved', value: '03' },
                { text: 'Close / Complete', value: '04' },
                { text: 'Canceled', value: '05' },
                { text: 'Closed', value: '06' },
            ],
            onFilter: (value: any, record: DataType) => record.status === value,
            sorter: (a: DataType, b: DataType) => (a.status || '').localeCompare(b.status || ''),
            sortDirections: ['descend', 'ascend'] as SortOrder[],
            render: (text: string, record: DataType) => getStatusText(record.status),
        },
        {
            title: 'TOP',
            dataIndex: 'top',
            key: 'top',
            hidden: true,
        },
        {
            title: 'Agreement',
            dataIndex: 'agreement_no',
            key: 'agreement_no',
            hidden: true,
        },
        {
            title: 'Credit Limit Approval Status',
            dataIndex: 'credit_limit_approval_status',
            key: 'credit_limit_approval_status',
            hidden: true,
        },
        {
            title: 'SUB Total',
            dataIndex: 'sub_total',
            key: 'sub_total',
            hidden: true,
        },
        {
            title: 'Item Disc',
            dataIndex: 'item_disc',
            key: 'item_disc',
            hidden: true,
        },
        {
            title: 'Total',
            dataIndex: 'total_a',
            key: 'total_a',
            hidden: true,
        },
        {
            title: 'VAT',
            dataIndex: 'vat',
            key: 'vat',
            hidden: true,
        },
        {
            title: 'Grand Total',
            dataIndex: 'grand_total',
            key: 'grand_total',
            hidden: true,
        },
        {
            title: 'Delivery Address1',
            dataIndex: 'delivery_address1',
            key: 'delivery_address1',
            hidden: true,
        },
        {
            title: 'Delivery Address2',
            dataIndex: 'delivery_address2',
            key: 'delivery_address2',
            hidden: true,
        },
        {
            title: 'Contact Person',
            dataIndex: 'contact_person',
            key: 'contact_person',
            hidden: true,
        },
        {
            title: 'Contact Phone',
            dataIndex: 'contact_phone',
            key: 'contact_phone',
            hidden: true,
        },
        {
            title: 'Deliver Via',
            dataIndex: 'deliver_via',
            key: 'deliver_via',
            hidden: true,
        },
        {
            title: 'Approval Remark',
            dataIndex: 'approval_remark',
            key: 'approval_remark',
            hidden: true,
        },
        {
            title: 'Outstanding Remark',
            dataIndex: 'outstanding_remark',
            key: 'outstanding_remark',
            hidden: true,
        },
        {
            title: 'Remark',
            dataIndex: 'remark_table',
            key: 'remark_table',
            hidden: true,
        },
        {
            title: 'Credit Limit Remaining',
            dataIndex: 'credit_limit_remaining',
            key: 'credit_limit_remaining',
            hidden: true,
        },
    ].filter(column => !(column as any).hidden);

    return {
        columns,
        tableData,
        searchInput,
        handleSearch,
        handleReset,
        searchText,
        searchedColumn,
        handleEdit,
        isLoading,
        error,
        mutate,
        showAllWarehouse,
        setShowAllWarehouse,
        userData
    };
};